import React, { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Input, Button, message, Spin, List, Typography } from 'antd';
import { CloudDownloadOutlined, LoadingOutlined, FontSizeOutlined, CopyOutlined } from '@ant-design/icons';
import NodeLabel from './NodeLabel';
import { TYPE_COLORS, getHandleStyle } from './nodeStyles';
import api from '../../../api/client';

const { Text } = Typography;

const FontExtractNode = ({ id, data, selected }) => {
    const [url, setUrl] = useState(data.url || '');
    const [loading, setLoading] = useState(false);
    const [fonts, setFonts] = useState(data.fonts || []);

    // Load fonts when they are available
    useEffect(() => {
        if (!fonts || fonts.length === 0) return;

        fonts.forEach(async (font, index) => {
            // Skip if already loaded (or if we don't have a url)
            if (font.loadedFamily || !font.url) return;

            const fontName = font.name.split('.')[0];
            const uniqueFontFamily = `Font_${id}_${fontName.replace(/[^a-zA-Z0-9]/g, '')}`;

            try {
                // Use proxy to bypass CORS
                const proxyUrl = `${api.defaults.baseURL}/storage/proxy?url=${encodeURIComponent(font.url)}`;
                console.log(`Loading font via proxy: ${uniqueFontFamily} from ${proxyUrl}`);

                const fontFace = new FontFace(uniqueFontFamily, `url("${proxyUrl}")`);
                const loadedFace = await fontFace.load();
                document.fonts.add(loadedFace);
                console.log(`Successfully loaded: ${uniqueFontFamily}`);

                // Update state immutably
                setFonts(currentFonts => {
                    return currentFonts.map(f => {
                        if (f.url === font.url) {
                            return { ...f, loadedFamily: uniqueFontFamily };
                        }
                        return f;
                    });
                });

            } catch (e) {
                console.error(`Failed to load font ${fontName}:`, e);
            }
        });
    }, [fonts, id]);

    const handleExtract = async () => {
        if (!url) {
            message.error('Please enter a URL');
            return;
        }

        setLoading(true);
        try {
            const response = await api.post('/fonts/extract', { url });
            if (response.data.success) {
                const extractedFonts = response.data.fonts;
                setFonts(extractedFonts);
                message.success(`Found ${extractedFonts.length} fonts!`);

                // Persist data if needed (via onSave prop if passed, but usually handled by parent or manual save)
                if (data.onSave) {
                    data.onSave(id, { url, fonts: extractedFonts });
                }
            } else {
                message.error('Extraction failed');
            }
        } catch (error) {
            console.error('Extraction error:', error);
            message.error('Failed to extract fonts');
        } finally {
            setLoading(false);
        }
    };

    const copyUrl = (url) => {
        navigator.clipboard.writeText(url);
        message.success('Font URL copied');
    };

    return (
        <div style={{
            background: 'transparent',
            padding: 20,
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
        }}>
            <CustomResizer
                isVisible={selected}
                minWidth={300}
                minHeight={200}
                onResizeEnd={(event, params) => {
                    const { width, height, x, y } = params;
                    if (data.onResize) {
                        data.onResize(id, { width, height, x, y });
                    }
                }}
            />
            <NodeLabel
                title={data.nodeTitle || 'Font Extractor'}
                iconType="font-size"
                color={data.nodeColor || '#eb2f96'}
                onTitleChange={(newTitle) => {
                    // Handle title change if needed or provided
                }}
            />

            <div style={{
                background: '#161b22',
                borderRadius: 8,
                border: '1px solid #30363d',
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: 10,
                flex: 1,
                height: '100%',
                overflow: 'hidden'
            }}>
                {/* Input Area */}
                <div style={{ display: 'flex', gap: 8 }}>
                    <Input
                        placeholder="https://example.com"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onBlur={() => {
                            if (data.onSave) data.onSave(id, { url });
                        }}
                        style={{
                            background: '#0d1117',
                            border: '1px solid #30363d',
                            color: '#c9d1d9'
                        }}
                    />
                    <Button
                        type="primary"
                        icon={loading ? <LoadingOutlined /> : <CloudDownloadOutlined />}
                        onClick={handleExtract}
                        loading={loading}
                    >
                        Extract
                    </Button>
                </div>

                {/* Fonts List */}
                <div className="nodrag" style={{
                    flex: 1,
                    overflowY: 'auto',
                    background: '#0d1117',
                    borderRadius: 4,
                    border: '1px solid #30363d',
                    padding: 8
                }}>
                    <List
                        dataSource={fonts}
                        renderItem={item => (
                            <List.Item
                                actions={[
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={<CopyOutlined />}
                                        onClick={() => copyUrl(item.url)}
                                    />
                                ]}
                                style={{ padding: '8px 0', borderBottom: '1px solid #30363d' }}
                            >
                                <div style={{ width: '100%' }}>
                                    <div style={{
                                        fontSize: 24,
                                        fontFamily: item.loadedFamily || 'inherit',
                                        color: '#fff',
                                        marginBottom: 4
                                    }}>
                                        {item.name}
                                    </div>
                                    <div style={{
                                        fontSize: 18,
                                        fontFamily: item.loadedFamily || 'inherit',
                                        color: '#8b949e',
                                        marginBottom: 4,
                                        wordBreak: 'break-all'
                                    }}>
                                        Aa Bb Cc Dd Ee Ff Gg Hh Ii Jj Kk Ll Mm Nn Oo Pp Qq Rr Ss Tt Uu Vv Ww Xx Yy Zz 0123456789
                                    </div>
                                    <div style={{ fontSize: 10, color: '#8b949e' }}>
                                        {item.format} • {(item.size / 1024).toFixed(1)} KB
                                    </div>
                                </div>
                            </List.Item>
                        )}
                    />
                    {fonts.length === 0 && !loading && (
                        <div style={{ textAlign: 'center', color: '#8b949e', marginTop: 20 }}>
                            No fonts extracted yet.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default memo(FontExtractNode);
